<template>
  <main class="audience-page">
    <section
      class="audience-hero"
      :style="heroStyle"
      aria-labelledby="audience-hero-title"
    >
      <div class="audience-hero__copy">
        <p class="audience-kicker">{{ page.hero.eyebrow }}</p>
        <h1 id="audience-hero-title">{{ page.hero.title }}</h1>
        <p class="audience-hero__lead">{{ page.hero.lead }}</p>

        <div class="audience-actions" aria-label="Primary actions">
          <NuxtLink class="audience-button audience-button--primary" :to="page.hero.primaryCta.to">
            <component :is="iconFor(page.hero.primaryCta.icon)" class="audience-button__icon" aria-hidden="true" />
            {{ page.hero.primaryCta.label }}
          </NuxtLink>
          <NuxtLink v-if="page.hero.secondaryCta" class="audience-button audience-button--secondary" :to="page.hero.secondaryCta.to">
            <component :is="iconFor(page.hero.secondaryCta.icon)" class="audience-button__icon" aria-hidden="true" />
            {{ page.hero.secondaryCta.label }}
          </NuxtLink>
        </div>

        <p class="audience-hero__caption">{{ page.hero.imageCaption }}</p>
        <span class="audience-sr-only">{{ page.hero.imageAlt }}</span>
      </div>
    </section>

    <section class="audience-stats" :aria-label="page.hero.eyebrow">
      <div v-for="stat in page.hero.stats" :key="stat.label">
        <span>{{ stat.label }}</span>
        <strong>{{ stat.value }}</strong>
      </div>
    </section>

    <section class="audience-section audience-section--intro" aria-labelledby="audience-fit-title">
      <div class="audience-section__copy">
        <p class="audience-kicker">{{ page.audience.label }}</p>
        <h2 id="audience-fit-title">{{ page.audience.painTitle }}</h2>
        <p>{{ page.audience.painText }}</p>
      </div>

      <aside class="audience-criteria" :aria-label="page.audience.criteriaTitle">
        <h3>{{ page.audience.criteriaTitle }}</h3>
        <ul>
          <li v-for="criterion in page.audience.criteria" :key="criterion">
            <Check class="audience-criteria__icon" aria-hidden="true" />
            <span>{{ criterion }}</span>
          </li>
        </ul>
      </aside>
    </section>

    <section
      v-for="section in page.sections"
      :key="section.title"
      class="audience-section audience-section--points"
      :aria-labelledby="sectionId(section.title)"
    >
      <div class="audience-section__copy">
        <p class="audience-kicker">{{ section.eyebrow }}</p>
        <h2 :id="sectionId(section.title)">{{ section.title }}</h2>
        <p>{{ section.body }}</p>
      </div>

      <div class="audience-point-grid">
        <article v-for="point in section.points" :key="point.title" class="audience-point">
          <component :is="iconFor(point.icon)" class="audience-point__icon" aria-hidden="true" />
          <h3>{{ point.title }}</h3>
          <p>{{ point.text }}</p>
        </article>
      </div>
    </section>

    <section class="audience-process" aria-labelledby="audience-process-title">
      <div class="audience-section__copy">
        <p class="audience-kicker">{{ page.process.eyebrow }}</p>
        <h2 id="audience-process-title">{{ page.process.title }}</h2>
      </div>

      <div class="audience-process__steps">
        <article v-for="(step, index) in page.process.steps" :key="step.title" class="audience-step">
          <span>{{ String(index + 1).padStart(2, '0') }}</span>
          <h3>{{ step.title }}</h3>
          <p>{{ step.text }}</p>
        </article>
      </div>
    </section>

    <section class="audience-proof" aria-labelledby="audience-proof-title">
      <div class="audience-proof__copy">
        <p class="audience-kicker">{{ page.proof.eyebrow }}</p>
        <h2 id="audience-proof-title">{{ page.proof.title }}</h2>
        <p>{{ page.proof.body }}</p>
        <div class="audience-text-links">
          <NuxtLink v-for="link in page.proof.links" :key="link.to" :to="link.to">
            <component :is="iconFor(link.icon)" aria-hidden="true" />
            {{ link.label }}
          </NuxtLink>
        </div>
      </div>

      <figure class="audience-proof__media">
        <NuxtImg
          :src="page.proof.image"
          :alt="page.proof.imageAlt"
          width="1200"
          height="820"
          sizes="sm:100vw md:48vw lg:42vw"
          loading="lazy"
        />
      </figure>
    </section>

    <section class="audience-cluster" aria-labelledby="audience-cluster-title">
      <div class="audience-section__copy">
        <p class="audience-kicker">{{ page.cluster.eyebrow }}</p>
        <h2 id="audience-cluster-title">{{ page.cluster.title }}</h2>
      </div>

      <div class="audience-cluster__grid">
        <NuxtLink v-for="link in page.cluster.links" :key="link.to" class="audience-cluster-card" :to="link.to">
          <span v-if="link.eyebrow">{{ link.eyebrow }}</span>
          <component :is="iconFor(link.icon)" class="audience-cluster-card__icon" aria-hidden="true" />
          <strong>{{ link.label }}</strong>
          <small v-if="link.description">{{ link.description }}</small>
        </NuxtLink>
      </div>
    </section>

    <section class="audience-faq" aria-labelledby="audience-faq-title">
      <div class="audience-section__copy">
        <p class="audience-kicker">FAQ</p>
        <h2 id="audience-faq-title">{{ page.locale === 'fr' ? 'Questions fréquentes' : 'Frequently asked questions' }}</h2>
      </div>

      <div class="audience-faq__items">
        <article v-for="item in page.faq" :key="item.question">
          <h3>{{ item.question }}</h3>
          <p>{{ item.answer }}</p>
        </article>
      </div>
    </section>

    <section class="audience-final" aria-labelledby="audience-final-title">
      <p class="audience-kicker">{{ page.finalCta.eyebrow }}</p>
      <h2 id="audience-final-title">{{ page.finalCta.title }}</h2>
      <p>{{ page.finalCta.body }}</p>
      <div class="audience-actions audience-actions--center" aria-label="Final actions">
        <NuxtLink class="audience-button audience-button--primary" :to="page.finalCta.primaryCta.to">
          <component :is="iconFor(page.finalCta.primaryCta.icon)" class="audience-button__icon" aria-hidden="true" />
          {{ page.finalCta.primaryCta.label }}
        </NuxtLink>
        <NuxtLink v-if="page.finalCta.secondaryCta" class="audience-button audience-button--secondary" :to="page.finalCta.secondaryCta.to">
          <component :is="iconFor(page.finalCta.secondaryCta.icon)" class="audience-button__icon" aria-hidden="true" />
          {{ page.finalCta.secondaryCta.label }}
        </NuxtLink>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
import {
  ArrowRight,
  CalendarCheck,
  Check,
  FileText,
  Gem,
  Gift,
  HeartHandshake,
  Ruler,
  Shirt,
  ShoppingBag,
  Sparkles,
  Store,
  Users,
  Wand2
} from 'lucide-vue-next'
import type { Component } from 'vue'
import type { AudienceSeoIcon, AudienceSeoPage } from '~/utils/audienceSeoPages'

const props = defineProps<{
  page: AudienceSeoPage
}>()

const iconMap: Record<AudienceSeoIcon | 'default', Component> = {
  calendar: CalendarCheck,
  default: ArrowRight,
  file: FileText,
  gift: Gift,
  gem: Gem,
  heart: HeartHandshake,
  ruler: Ruler,
  shirt: Shirt,
  sparkles: Sparkles,
  store: Store,
  users: Users,
  wand: Wand2
}

const iconFor = (icon?: AudienceSeoIcon): Component => iconMap[icon || 'default'] || ShoppingBag

const siteUrl = 'https://kendrick.paris'
const canonicalUrl = computed(() => `${siteUrl}${props.page.canonicalPath}`)
const alternateUrl = computed(() => props.page.alternatePath ? `${siteUrl}${props.page.alternatePath}` : null)
const language = computed(() => props.page.locale === 'fr' ? 'fr-FR' : 'en-US')
const alternateLanguage = computed(() => props.page.locale === 'fr' ? 'en-US' : 'fr-FR')
const homeUrl = computed(() => props.page.locale === 'fr' ? `${siteUrl}/fr` : siteUrl)
const heroImagePosition = computed(() =>
  props.page.hero.imagePosition || (props.page.hero.image === '/k/pumps/kendrick-pumps-beige-1.jpg' ? 'center bottom' : 'center')
)
const heroStyle = computed(() => ({
  '--audience-hero-image': `url('${props.page.hero.image}')`,
  '--audience-hero-position': heroImagePosition.value
}))

const sectionId = (title: string) => `audience-${title.toLowerCase().normalize('NFD').replace(/[\u0300-\u036F]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`

const jsonLd = computed(() => [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: props.page.metaTitle,
    description: props.page.metaDescription,
    url: canonicalUrl.value,
    inLanguage: language.value,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Kendrick Paris',
      url: siteUrl
    },
    about: {
      '@type': 'Brand',
      name: 'Kendrick Paris',
      url: `${siteUrl}${props.page.locale === 'fr' ? '/fr/brand' : '/brand'}`
    }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: props.page.locale === 'fr' ? 'Accueil' : 'Home',
        item: homeUrl.value
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: props.page.breadcrumbLabel,
        item: canonicalUrl.value
      }
    ]
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: props.page.faq.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  }
])

useHead(() => ({
  htmlAttrs: { lang: language.value },
  title: props.page.metaTitle,
  meta: [
    { name: 'description', content: props.page.metaDescription },
    { name: 'keywords', content: props.page.keywords },
    { name: 'robots', content: 'index, follow' },
    { property: 'og:title', content: props.page.metaTitle },
    { property: 'og:description', content: props.page.metaDescription },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'Kendrick Paris' },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:image', content: `${siteUrl}${props.page.hero.image}` },
    { property: 'og:image:alt', content: props.page.hero.imageAlt },
    { property: 'og:locale', content: language.value.replace('-', '_') },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: props.page.metaTitle },
    { name: 'twitter:description', content: props.page.metaDescription },
    { name: 'twitter:image', content: `${siteUrl}${props.page.hero.image}` }
  ],
  link: [
    { rel: 'canonical', href: canonicalUrl.value },
    { rel: 'alternate', hreflang: language.value, href: canonicalUrl.value },
    ...(alternateUrl.value ? [{ rel: 'alternate', hreflang: alternateLanguage.value, href: alternateUrl.value }] : []),
    { rel: 'alternate', hreflang: 'x-default', href: props.page.locale === 'fr' && alternateUrl.value ? alternateUrl.value : canonicalUrl.value }
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify(jsonLd.value)
    }
  ]
}))
</script>

<style scoped>
.audience-page {
  --ink: #12110f;
  --muted: #6c655d;
  --paper: #f7f3ed;
  --soft: #ebe5da;
  --line: #d9d0c4;
  --accent: #8b2420;
  --green: #30483e;
  --blue: #263a5c;
  --serif: 'Playfair Display', Georgia, serif;
  --sans: Inter, 'Helvetica Neue', Arial, sans-serif;

  background: var(--paper);
  color: var(--ink);
  font-family: var(--sans);
}

.audience-hero {
  min-height: min(900px, 100svh);
  display: flex;
  align-items: flex-end;
  padding: clamp(7rem, 11vw, 10rem) clamp(1.25rem, 5vw, 5rem) clamp(3.5rem, 7vw, 6rem);
  background-image:
    linear-gradient(90deg, rgba(14, 13, 12, 0.94), rgba(14, 13, 12, 0.68) 46%, rgba(14, 13, 12, 0.18)),
    var(--audience-hero-image);
  background-position:
    center,
    var(--audience-hero-position);
  background-size:
    auto,
    cover;
  background-repeat: no-repeat;
  color: #fff9ef;
}

@media (min-width: 1400px) {
  .audience-hero {
    min-height: min(62vw, 100svh);
  }
}

.audience-hero__copy {
  max-width: 60rem;
}

.audience-kicker {
  margin: 0 0 0.9rem;
  color: currentColor;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.24em;
  line-height: 1.35;
  opacity: 0.62;
  text-transform: uppercase;
}

.audience-hero h1,
.audience-section__copy h2,
.audience-process h2,
.audience-proof h2,
.audience-cluster h2,
.audience-faq h2,
.audience-final h2 {
  margin: 0;
  font-family: var(--serif);
  font-weight: 500;
  letter-spacing: 0;
}

.audience-hero h1 {
  max-width: 16ch;
  font-size: clamp(3.15rem, 6.4vw, 6.7rem);
  line-height: 0.94;
  text-wrap: balance;
}

.audience-hero__lead {
  max-width: 45rem;
  margin: 1.4rem 0 0;
  color: rgba(255, 249, 239, 0.8);
  font-size: clamp(1.05rem, 1.6vw, 1.34rem);
  line-height: 1.72;
}

.audience-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 2rem;
}

.audience-actions--center {
  justify-content: center;
  width: 100%;
}

.audience-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3.05rem;
  gap: 0.55rem;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 0.9rem 1.1rem;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;
}

.audience-button--primary {
  background: #fff9ef;
  color: var(--ink);
}

.audience-button--secondary {
  border-color: rgba(255, 249, 239, 0.38);
  color: #fff9ef;
}

.audience-button__icon {
  width: 1rem;
  height: 1rem;
  flex: 0 0 auto;
}

.audience-hero__caption {
  max-width: 31rem;
  margin: 1.3rem 0 0;
  color: rgba(255, 249, 239, 0.66);
  font-size: 0.86rem;
  line-height: 1.45;
}

.audience-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

.audience-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  background: #fff;
}

.audience-stats div {
  padding: clamp(1rem, 3vw, 1.5rem) clamp(1rem, 4vw, 5rem);
  border-right: 1px solid var(--line);
}

.audience-stats div:last-child {
  border-right: 0;
}

.audience-stats span,
.audience-stats strong {
  display: block;
}

.audience-stats span {
  color: var(--muted);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.audience-stats strong {
  margin-top: 0.4rem;
  font-family: var(--serif);
  font-size: clamp(1.55rem, 3vw, 2.4rem);
  font-weight: 500;
}

.audience-section,
.audience-process,
.audience-proof,
.audience-cluster,
.audience-faq,
.audience-final {
  padding: clamp(3.75rem, 8vw, 6.5rem) clamp(1.25rem, 5vw, 5rem);
}

.audience-section {
  display: grid;
  gap: 2rem;
}

@media (min-width: 920px) {
  .audience-section {
    grid-template-columns: minmax(0, 0.85fr) minmax(380px, 1fr);
    align-items: start;
  }
}

.audience-section--intro {
  background: #f0ede7;
}

.audience-section--points:nth-of-type(odd) {
  background: #fff;
}

.audience-section__copy {
  max-width: 48rem;
}

.audience-section__copy h2,
.audience-process h2,
.audience-proof h2,
.audience-cluster h2,
.audience-faq h2,
.audience-final h2 {
  max-width: 17ch;
  font-size: clamp(2.35rem, 5vw, 4.35rem);
  line-height: 0.98;
}

.audience-section__copy p:not(.audience-kicker),
.audience-proof p,
.audience-final p {
  max-width: 43rem;
  margin: 1.15rem 0 0;
  color: var(--muted);
  font-size: 1rem;
  line-height: 1.78;
}

.audience-criteria,
.audience-point,
.audience-step,
.audience-faq article {
  border: 1px solid var(--line);
  border-radius: 6px;
  background: #fff;
}

.audience-criteria {
  padding: clamp(1.1rem, 3vw, 1.5rem);
}

.audience-criteria h3 {
  margin: 0;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.audience-criteria ul {
  display: grid;
  gap: 0.8rem;
  margin: 1.1rem 0 0;
  padding: 0;
  list-style: none;
}

.audience-criteria li {
  display: grid;
  grid-template-columns: 1.1rem minmax(0, 1fr);
  gap: 0.65rem;
  color: #4a453f;
  line-height: 1.55;
}

.audience-criteria__icon {
  width: 1rem;
  height: 1rem;
  margin-top: 0.18rem;
  color: var(--green);
}

.audience-point-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 16rem), 1fr));
  gap: 0.85rem;
}

.audience-point {
  padding: 1.25rem;
}

.audience-point__icon,
.audience-cluster-card__icon {
  width: 1.25rem;
  height: 1.25rem;
  color: var(--accent);
}

.audience-point h3,
.audience-step h3,
.audience-faq h3 {
  margin: 0.85rem 0 0;
  font-size: 1rem;
  line-height: 1.35;
}

.audience-point p,
.audience-step p,
.audience-faq p,
.audience-cluster-card small {
  color: var(--muted);
  font-size: 0.95rem;
  line-height: 1.68;
}

.audience-process {
  background: #11100e;
  color: #f8f1e8;
}

.audience-process .audience-section__copy p {
  color: rgba(248, 241, 232, 0.66);
}

.audience-process__steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 15rem), 1fr));
  gap: 0.85rem;
  margin-top: 2rem;
}

.audience-step {
  border-color: rgba(255, 249, 239, 0.14);
  background: #1c1a17;
  padding: 1.25rem;
}

.audience-step span {
  color: #d7b1a9;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.18em;
}

.audience-step p {
  color: rgba(248, 241, 232, 0.68);
}

.audience-proof {
  display: grid;
  gap: 2rem;
  background: #f2f4ef;
}

@media (min-width: 920px) {
  .audience-proof {
    grid-template-columns: minmax(0, 0.82fr) minmax(360px, 0.86fr);
    align-items: center;
  }
}

.audience-proof__media {
  overflow: hidden;
  margin: 0;
  border: 1px solid var(--line);
  background: #fff;
}

.audience-proof__media img {
  display: block;
  width: 100%;
  aspect-ratio: 3 / 2;
  height: auto;
  object-fit: cover;
}

.audience-text-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  margin-top: 1.5rem;
}

.audience-text-links a {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: var(--accent);
  font-size: 0.9rem;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 0.18em;
}

.audience-text-links svg {
  width: 1rem;
  height: 1rem;
}

.audience-cluster {
  background: #fff;
}

.audience-cluster__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 17rem), 1fr));
  gap: 0.85rem;
  margin-top: 2rem;
}

.audience-cluster-card {
  display: grid;
  gap: 0.65rem;
  align-content: start;
  min-height: 13.5rem;
  border: 1px solid var(--line);
  border-radius: 6px;
  padding: 1.2rem;
  color: inherit;
  text-decoration: none;
  transition: border-color 0.18s ease, transform 0.18s ease;
}

.audience-cluster-card:hover {
  border-color: rgba(139, 36, 32, 0.42);
  transform: translateY(-2px);
}

.audience-cluster-card span {
  color: var(--muted);
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.audience-cluster-card strong {
  font-size: 1.08rem;
  line-height: 1.25;
}

.audience-faq {
  background: var(--soft);
}

.audience-faq__items {
  display: grid;
  gap: 0.85rem;
  margin-top: 2rem;
}

.audience-faq article {
  padding: 1.2rem;
}

.audience-faq h3 {
  margin-top: 0;
}

.audience-final {
  display: grid;
  justify-items: center;
  background: #13110f;
  color: #fff9ef;
  text-align: center;
}

.audience-final h2 {
  max-width: 15ch;
}

.audience-final p {
  color: rgba(255, 249, 239, 0.72);
}

@media (max-width: 700px) {
  .audience-hero {
    padding-top: 6.5rem;
  }

  .audience-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .audience-button {
    width: 100%;
    padding-right: 0.8rem;
    padding-left: 0.8rem;
    font-size: 0.68rem;
  }

  .audience-stats {
    grid-template-columns: 1fr;
  }

  .audience-stats div {
    border-right: 0;
    border-bottom: 1px solid var(--line);
  }

  .audience-stats div:last-child {
    border-bottom: 0;
  }
}
</style>
